import { useState, useCallback, useRef, useEffect } from 'react';
import { Notification } from '../app/dashboard/interfaces/notifications.interfaces';

const SHOW_TIME = 5000; // 5s

export const useToastNotifications = (afterTimeout?: (id: string) => void) => {
  const [toasts, setToasts] = useState<Notification[]>([]);
  const timers = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const hoverStates = useRef<Map<string, boolean>>(new Map());

  const removeToast = useCallback(
    (id: string) => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
      clearTimeout(timers.current.get(id));
      timers.current.delete(id);
      hoverStates.current.delete(id);
      afterTimeout && afterTimeout(id);
    },
    [afterTimeout],
  );

  const showToast = useCallback(
    (notification: Notification) => {
      const toastId = notification.id ?? Date.now().toString();

      if (toasts.some((t) => t.id === toastId)) return;

      setToasts((prev) => [...prev, { ...notification, id: toastId }]);

      const timer = setTimeout(() => {
        if (!hoverStates.current.get(toastId)) {
          removeToast(toastId);
        }
      }, SHOW_TIME);

      timers.current.set(toastId, timer);
    },
    [removeToast, toasts],
  );

  const onMouseEnter = (id: string) => {
    hoverStates.current.set(id, true);
    clearTimeout(timers.current.get(id));
  };

  const onMouseLeave = (id: string) => {
    hoverStates.current.set(id, false);
    const timer = setTimeout(() => removeToast(id), SHOW_TIME);
    timers.current.set(id, timer);
  };

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      timers.current.forEach(clearTimeout);
    };
  }, []);

  return {
    toasts,
    showToast,
    removeToast,
    onMouseEnter,
    onMouseLeave,
  };
};
